import { Button, Tooltip, Chip, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { setSelectedUnit } from '@/features/project/projectSlice';
import { getLongNumberStr, convertPseudoDateToRealDate } from '@/helpers/common';
import {
  getLeaseCategory,
  getCategoryColor,
  getCategoryLabel,
  formatLeaseLength,
  calculateTotalFees,
  calculateTotalConcessions,
  calculateEffectiveMonthlyRent,
  calculateAnnualRentPerSqft,
  calculateEffectiveAnnualRentPerSqft,
} from '@/helpers/leaseHelpers';
import WorkflowsProvider, { CustomAction } from '../BuildingWorkflows/WorkflowsProvider';
import { Process, Unit, Lease } from '@urbanmix-tech/shared-js';
import { useState } from 'react';
import { LeaseEditDialog } from './LeaseEditDialog';
import { DisplayProcess } from './ExpiringLeaseItem';
import { selectUnitStatusById } from '@/features/project/unitStatusSelectors';
import { UnitStatusBadge, hasStatusChips } from '@/components/UnitStatus/UnitStatusBadge';

type LeaseCardProps = {
  unit: Unit;
  lease: Lease;
  processes: Process[];
  isHovered?: boolean;
  onHoverUnit?: (unitId: string | null) => void;
  onSelectUnit?: (unitId: string | null) => void;
  onWorkflowEnd?: (unitId: string | null) => void;
  showEdit?: boolean;
  showCategory?: boolean;
  showActions?: boolean;
  compact?: boolean;
  borderColorOverride?: string;
  onLeaseUpdated?: (lease: Lease) => void;
  onDelete?: (leaseId: string) => void;
  hasDraftLease?: boolean;
  // Process-related props for expiring leases view
  displayProcess?: DisplayProcess[];
  daysToExpire?: number;
  expirationDate?: Date;
};

export function LeaseCard({
  unit,
  lease,
  processes,
  isHovered = false,
  onHoverUnit,
  onSelectUnit,
  onWorkflowEnd,
  showEdit = false,
  showCategory = false,
  showActions = false,
  compact = false,
  borderColorOverride,
  onLeaseUpdated,
  onDelete,
  hasDraftLease = false,
  displayProcess,
  daysToExpire,
  expirationDate,
}: LeaseCardProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { buildingId } = useParams<{ buildingId: string }>();
  const visualizer = useSelector((state: any) => state.project.projectVisualizer);
  const [hovering, setHovering] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const leaseCategory = getLeaseCategory(lease);
  const canDelete = !!onDelete;

  // Live unit status, folded from the project's `state_*` feed. An ordinary
  // occupied unit renders no chips, so gate the header on there being some.
  const unitStatus = useSelector(selectUnitStatusById)[unit?.id];
  const showStatus = !!unitStatus && hasStatusChips(unitStatus);

  // Custom action to navigate to pricing tool with this unit
  const customActions: CustomAction[] = showActions && buildingId && unit?.projectId ? [
    {
      id: 'price-unit',
      name: 'Price Unit',
      onClick: () => {
        navigate(`/m/projects/${unit.projectId}/${buildingId}/pricing/${unit.id}`);
      },
    },
  ] : [];

  // Get process status info for display
  const currentProcess = displayProcess && displayProcess.length > 0 ? displayProcess[0] : null;
  const completedProcess = displayProcess?.find((p) => p.process.status === 'completed');
  const processStatus = currentProcess?.process.status;
  const processOutcome = completedProcess?.outcome;
  const processVisualValue = completedProcess ? completedProcess.completionEvent?.eventType ?? 'completed' : 'running';

  // Calculate all derived values from lease
  const leaseLengthDisplay = formatLeaseLength(lease);
  const totalFees = calculateTotalFees(lease);
  const totalConcessions = calculateTotalConcessions(lease);
  const effectiveMonthlyRent = calculateEffectiveMonthlyRent(lease);
  const annualRentPerSqft = unit.sqft ? calculateAnnualRentPerSqft(lease, unit.sqft) : 0;
  const effectiveAnnualRentPerSqft = unit.sqft ? calculateEffectiveAnnualRentPerSqft(lease, unit.sqft) : 0;

  // Get the value being visualized from apartmentData
  const apartmentData = useSelector((state: any) => state.project.apartmentData);
  const currentApartmentData = apartmentData?.find((data: any) => data.apartmentId === unit.id);
  const valueToVisualize = currentApartmentData?.value;

  // Border color priority: override > visualizer > default
  const borderColor = borderColorOverride ||
    (visualizer?.getColor ? visualizer.getColor(valueToVisualize) : '#666666');

  // Format dates
  const formatStartDate = (date: Date | undefined | any) => {
    const realDate = convertPseudoDateToRealDate(date);
    if (!realDate) return 'N/A';
    return realDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatEndDate = (date: Date | undefined | any) => {
    const realDate = convertPseudoDateToRealDate(date);
    if (!realDate) return 'N/A';
    return realDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const startDate = formatStartDate(lease?.leaseStartDate);
  const endDate = formatEndDate(lease?.leaseEndDate);

  return (
    <div
      className="w-full rounded-md border border-zinc-600 px-6 py-4 mr-8 cursor-pointer transition-colors"
      onMouseEnter={() => {
        setHovering(true);
        onHoverUnit && onHoverUnit(unit.id);
      }}
      onMouseLeave={() => {
        setHovering(false);
        onHoverUnit && onHoverUnit(null);
      }}
      style={{
        borderColor: borderColor,
        borderWidth: '2px',
        backgroundColor: hovering ? '#222222' : '#1a1a1a',
      }}
    >
      {/* Header with status/category badges and edit button */}
      {(showCategory || showEdit || hasDraftLease || showStatus) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {showStatus && <UnitStatusBadge status={unitStatus} />}
            {showCategory && (
              <>
                <Chip
                  label={getCategoryLabel(getLeaseCategory(lease))}
                  size="small"
                  style={{
                    backgroundColor: getCategoryColor(getLeaseCategory(lease)),
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
                {lease.status === 'canceled' && (
                  <Chip
                    label="Canceled"
                    size="small"
                    style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                    }}
                  />
                )}
              </>
            )}
            {hasDraftLease && (
              <Chip
                label="Draft Lease Available"
                size="small"
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {showEdit && (
              <Tooltip title="Edit lease">
                <IconButton
                  size="small"
                  onClick={() => setEditDialogOpen(true)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'white' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete lease">
                <IconButton
                  size="small"
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {lease.id && (
              <Typography variant="caption" className="text-zinc-400">
                ID: {lease.id.slice(0, 8)}...
              </Typography>
            )}
          </div>
        </div>
      )}

      {/* Main content row with two distinct columns */}
      <div className="flex min-h-20 flex-row items-stretch gap-6">
        {/* Left column - unit info and rent details */}
        <div className="flex flex-1 flex-row items-center gap-12">
          {/* Unit name and badge section */}
          <div className="flex flex-row items-center gap-3">
            <Button
              variant="text"
              className="text-white"
              onClick={() =>
                onSelectUnit ? onSelectUnit(unit.id) : dispatch(setSelectedUnit({ apartmentId: unit.id }))
              }
            >
              <span className="text-lg font-semibold">{unit.name || 'N/A'}</span>
            </Button>

            {/* Affordable unit indicator - fixed width for alignment */}
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              {unit.affordable && (
                <Tooltip title="Affordable">
                  <ShieldOutlinedIcon className="text-zinc-400" fontSize="small" />
                </Tooltip>
              )}
            </div>
          </div>

          {/* Rent info */}
          <div className="flex flex-col gap-1 flex-1">
            <div className="text-xs text-zinc-400">Rent</div>
            <div className="text-sm font-semibold">${getLongNumberStr(lease.monthlyRent)}</div>
          </div>

          {/* Unit details - Sqft */}
          <div className="flex flex-col gap-1 flex-1">
            <div className="text-xs text-zinc-400">Sqft</div>
            <div className="text-sm">{unit.sqft || 'N/A'}</div>
          </div>

          {/* Unit details - AR/sqft */}
          {annualRentPerSqft > 0 && (
            <div className="flex flex-col gap-1 flex-1">
              <div className="text-xs text-zinc-400">AR/sqft</div>
              <div className="text-sm">${annualRentPerSqft.toFixed(2)}</div>
            </div>
          )}
        </div>

        {/* Vertical divider */}
        <div className="w-px bg-zinc-700"></div>

        {/* Right column - lease dates OR days to expiration in compact mode */}
        <div className="flex flex-1 flex-row items-center justify-end gap-8 pl-6">
          {compact && daysToExpire !== undefined ? (
            // Compact mode: show days to expiration
            <div className="flex flex-col gap-1 text-right">
              <div className="text-xs text-zinc-400">Expires In</div>
              <div className="text-sm">
                {daysToExpire} days
                {expirationDate && (
                  <span className="text-zinc-400 ml-2">
                    ({expirationDate.toLocaleDateString()})
                  </span>
                )}
              </div>
            </div>
          ) : (
            // Full mode: show start and end dates
            <>
              <div className="flex flex-col gap-1 text-right">
                <div className="text-xs text-zinc-400">Start Date</div>
                <div className="text-sm">{startDate}</div>
              </div>
              <div className="flex flex-col gap-1 text-right">
                <div className="text-xs text-zinc-400">End Date</div>
                <div className="text-sm">{endDate}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Secondary details row - hidden in compact mode */}
      {!compact && (
        <div className="flex flex-row gap-6 mt-4 pt-4 border-t border-zinc-700 text-xs">
          <div className="flex flex-col gap-1">
            <div className="text-zinc-400">Security Deposit</div>
            <div className="text-sm">${getLongNumberStr(lease.securityDeposit || 0)}</div>
          </div>
          {totalFees > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-zinc-400">Additional Fees</div>
              <div className="text-sm">${getLongNumberStr(totalFees)}</div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className="text-zinc-400">Lease Length</div>
            <div className="text-sm">{leaseLengthDisplay}</div>
          </div>
          {totalConcessions > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-zinc-400">Concessions</div>
              <div className="text-sm">${getLongNumberStr(totalConcessions)}</div>
            </div>
          )}
        </div>
      )}

      {/* Tertiary details row - hidden in compact mode */}
      {!compact && (
        <div className="flex flex-row gap-6 mt-4 pt-4 border-t border-zinc-700 text-xs">
          <div className="flex flex-col gap-1">
            <div className="text-zinc-400">Eff. Monthly Rent</div>
            <div className="text-sm">${getLongNumberStr(effectiveMonthlyRent)}</div>
          </div>
          {effectiveAnnualRentPerSqft > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-zinc-400">Eff. Annual Rent/Sqft</div>
              <div className="text-sm">${effectiveAnnualRentPerSqft.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {/* Process and actions row - shown when showActions is true */}
      {showActions && (
        <div className="flex min-h-12 flex-row items-center gap-4 mt-4 pt-4 border-t border-zinc-700">
          {/* Process status display */}
          {currentProcess && (
            <div className="flex flex-row gap-8 text-sm text-zinc-200 items-center">
              <div style={{ color: visualizer?.getColor ? visualizer.getColor(processVisualValue) : '#666666' }}>
                {processStatus === 'start' && <AutorenewOutlinedIcon />}
                {processStatus === 'completed' && <CheckCircleOutlinedIcon />}
                {processStatus === 'cancelled' && <CancelOutlinedIcon />}
              </div>
              {processOutcome ? (
                <div>{processOutcome}</div>
              ) : (
                <div className="flex flex-row gap-8">
                  <div>{currentProcess.process.name}</div>
                  {currentProcess.lastActionItem?.title && (
                    <>
                      <div>|</div>
                      <div>{currentProcess.lastActionItem.title}</div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Spacer to push actions to the right */}
          <div className="flex-1"></div>

          {/* Workflow actions dropdown */}
          <div className="flex-shrink-0">
            <WorkflowsProvider
              projectId={unit.projectId}
              unitId={unit.id}
              variant="dropdown"
              customActions={customActions}
              onWorkflowEnd={() => {
                onWorkflowEnd && onWorkflowEnd(unit.id);
              }}
            />
          </div>
        </div>
      )}

      {/* Lease Edit Dialog */}
      {showEdit && (
        <LeaseEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          lease={lease}
          selectedUnit={unit}
          onLeaseUpdated={onLeaseUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Lease</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this {leaseCategory} lease for unit {unit.name || unit.id}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDelete?.(lease.id);
              setDeleteDialogOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
